import { useCallback, useEffect, useMemo, useRef } from "react";

/**
 * Aborted on unmount
 */
export function useAbortController() {
  const controller = useMemo(() => new AbortController(), []);

  useEffect(() => {
    return () => controller.abort();
  }, [controller]);

  return controller;
}

/**
 * For fetches re-run on filter changes: each call aborts the previous request and returns a fresh signal
 * @returns function yielding the signal for the next request, and one aborting the current request; the last one is aborted on unmount
 */
export function useAbortPrevious(): [nextSignal: () => AbortSignal, abort: () => void] {
  const controller = useRef<AbortController>(undefined);

  const abort = useCallback(() => controller.current?.abort(), []);

  useEffect(() => abort, [abort]);

  const nextSignal = useCallback(() => {
    abort();
    controller.current = new AbortController();
    return controller.current.signal;
  }, [abort]);

  return [nextSignal, abort];
}
